import React, {
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  DocumentPreviewModalDeprecated,
  resolveByUrl,
} from './document-preview-modal';
import { UploadFile } from 'antd';
import { SimpleFileFragmentType } from '@common/graphql';

type PreviewAsset =
  | { kind: 'uploadFile'; data: UploadFile }
  | { kind: 'uploadedFile'; data: SimpleFileFragmentType }
  | { kind: 'url'; data: string };

type ContextValue = {
  handleOpen: (asset: PreviewAsset) => void;
  handleClose: () => void;
};

const PreviewModalCtx = React.createContext<ContextValue>({
  handleOpen: () => {},
  handleClose: () => {},
});

type Props = {
  children?: ReactNode | undefined;
};

export const useDocumentPreviewModalDeprecated = () =>
  useContext(PreviewModalCtx);

const resolvePreviewAsset = (asset: PreviewAsset) => {
  switch (asset.kind) {
    case 'uploadFile':
      return {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        url: URL.createObjectURL(asset.data.originFileObj!),
        embed: asset.data.type === 'application/pdf',
      };
    case 'uploadedFile':
      return {
        url: asset.data.downloadUrl || '',
        embed: asset.data.contentType === 'application/pdf',
      };
    case 'url':
      return resolveByUrl(asset.data);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore: we're in js land, this scenario is possible.
  throw new Error(`Unhandled asset kind ${asset?.kind}`);
};

export const DocumentPreviewModalDeprecatedProvider = ({ children }: Props) => {
  const [openAssets, setOpenAssets] = useState<PreviewAsset[]>([]);

  const handleOpen = useCallback((asset: PreviewAsset) => {
    setOpenAssets([asset]);
  }, []);

  const handleClose = useCallback(() => {
    setOpenAssets([]);
  }, []);

  const value = useMemo(
    () => ({
      handleOpen,
      handleClose,
    }),
    [handleClose, handleOpen]
  );

  return (
    <PreviewModalCtx.Provider value={value}>
      {children}
      <DocumentPreviewModalDeprecated
        assets={openAssets}
        visible={!!openAssets.length}
        onCancel={handleClose}
        resolveAsset={resolvePreviewAsset}
      />
    </PreviewModalCtx.Provider>
  );
};
